import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PendingSuggestion } from "./ai-edit-bubble"
import type { Editor } from "@tiptap/react"

import { Check, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { aiEditHighlightKey, type AiEditHighlightState } from "../tiptap/ai-edit-highlight"

type InlineSuggestionActionsProps = {
  editor: Editor | null
  pendingSuggestions: Map<string, PendingSuggestion>
  containerRef: React.RefObject<HTMLDivElement | null>
  onAccept: (suggestionId: string) => void
  onDiscard: (suggestionId: string) => void
}

type SuggestionPosition = {
  id: string
  top: number
  left: number
}

export function InlineSuggestionActions({
  editor,
  pendingSuggestions,
  containerRef,
  onAccept,
  onDiscard,
}: InlineSuggestionActionsProps) {
  const { t } = useTranslation()
  const [positions, setPositions] = useState<SuggestionPosition[]>([])
  const rafRef = useRef<number | null>(null)

  const calculatePositions = useCallback(() => {
    if (!editor || !containerRef.current || pendingSuggestions.size === 0) {
      setPositions([])
      return
    }

    const highlightState = aiEditHighlightKey.getState(editor.state) as AiEditHighlightState | null
    if (!highlightState || highlightState.size === 0) {
      setPositions([])
      return
    }

    const containerRect = containerRef.current.getBoundingClientRect()
    const newPositions: SuggestionPosition[] = []

    for (const [suggestionId] of pendingSuggestions) {
      const ranges = highlightState.get(suggestionId)
      if (!ranges || ranges.length === 0) continue

      const firstRange = ranges[0]
      try {
        const coords = editor.view.coordsAtPos(firstRange.from)
        const top = coords.top - containerRect.top
        const left = containerRect.width - 8

        newPositions.push({
          id: suggestionId,
          top,
          left,
        })
      } catch {
        continue
      }
    }

    setPositions(newPositions)
  }, [editor, containerRef, pendingSuggestions])

  useEffect(() => {
    const updatePositions = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(calculatePositions)
    }

    updatePositions()

    if (!editor) return

    const container = containerRef.current?.closest(".overflow-y-auto")
    container?.addEventListener("scroll", updatePositions)
    window.addEventListener("resize", updatePositions)

    editor.on("transaction", updatePositions)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      container?.removeEventListener("scroll", updatePositions)
      window.removeEventListener("resize", updatePositions)
      editor.off("transaction", updatePositions)
    }
  }, [editor, containerRef, calculatePositions])

  if (positions.length === 0) return null

  return (
    <>
      {positions.map((position) => (
        <div
          key={position.id}
          className={cn(
            "bg-background/95 absolute z-20 flex items-center gap-0.5 rounded-md border p-0.5 shadow-sm backdrop-blur transition-opacity",
            "hover:shadow-md"
          )}
          style={{
            top: position.top,
            right: 8,
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-success hover:bg-success/10 hover:text-success size-6"
                size="icon"
                variant="ghost"
                onClick={() => onAccept(position.id)}
              >
                <Check className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("canvas.inline_accept", { defaultValue: "Accept change" })}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-destructive hover:bg-destructive/10 hover:text-destructive size-6"
                size="icon"
                variant="ghost"
                onClick={() => onDiscard(position.id)}
              >
                <X className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t("canvas.inline_discard", { defaultValue: "Discard change" })}
            </TooltipContent>
          </Tooltip>
        </div>
      ))}
    </>
  )
}
